import { useEffect, useRef, useState } from "react";
import {
  AsyncState,
  ConsumerAppHeader,
  ConsumerButton,
  ConsumerContainer,
  ConsumerIcon,
  ConsumerR2Theme,
  StatusBadge,
} from "../shared/index.js";
import { consumerJourneyAdapter, hasValue } from "../adapters/journey/index.js";
import { consumerSupportAdapter } from "../adapters/support";
import RecheckOptInDialog from "../support/RecheckOptInDialog";
import ConsumerJourneyMap from "./ConsumerJourneyMap";
import "../support/support.css";
import "./journey.css";

const SEGMENT_COPY = {
  ACCESS: ["대여소까지 이동", "ACCESS", "transit"],
  RENT: ["따릉이 대여", "RENT", "bike"],
  RIDE: ["자전거 이동", "RIDE", "ride"],
  VISIT: ["장소 방문", "VISIT", "mapPin"],
};

const ERROR_COPY = {
  PREMIUM_REQUIRED: "현재 조건으로 다시 계획하려면 Premium 활성 계정이 필요합니다.",
  PREMIUM_ENTITLEMENT_UNAVAILABLE: "Premium 상태를 지금 확인할 수 없습니다.",
  JOURNEY_REVISION_CONFLICT: "다른 재계획 결과가 있습니다. 최신 계획을 다시 불러와 주세요.",
  SAVED_ROUTE_LIMIT_REACHED: "저장한 AI 계획은 최대 10개입니다.",
  IDEMPOTENCY_CONFLICT: "같은 저장 요청이 충돌했습니다. 잠시 후 다시 시도해 주세요.",
};

const THEME_OPTIONS = [
  ["PARK", "공원"], ["RIVER", "한강·하천"], ["CAFE", "카페"],
  ["ATTRACTION", "명소"], ["CULTURE", "문화"], ["FOOD", "음식"],
];

const ROUTE_MODE_OPTIONS = [
  ["BIKE_ONLY", "자전거 우선"], ["ACCESSIBLE", "접근성 우선"], ["SHORTEST", "최단 거리"],
];

function present(value, formatter = String) {
  return hasValue(value) ? formatter(value) : "확인되지 않음";
}

function formatDuration(seconds) {
  return present(seconds, (value) => `${Math.round(Number(value) / 60)}분`);
}

function formatDistance(meters) {
  return present(meters, (value) => Number(value) >= 1000 ? `${(Number(value) / 1000).toFixed(1)}km` : `${Number(value)}m`);
}

function formatProbability(value) {
  return present(value, (raw) => { const numeric = Number(raw); return `${Number(((numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric)).toFixed(1))}%`; });
}

function formatTime(value) {
  if (!value) return "확인되지 않음";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "확인되지 않음";
  return new Intl.DateTimeFormat("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
}

function evidenceEntries(evidence = {}) {
  return Object.entries(evidence).flatMap(([kind, values]) => Object.values(values || {}).map((item) => ({ ...item, kind })));
}

function evidenceById(evidence = {}, id) {
  if (!id) return null;
  return evidenceEntries(evidence).find((item) => item.evidenceId === id) || null;
}

function evidenceTitle(item) {
  return item?.textFacts?.displayName || item?.textFacts?.stationName || item?.textFacts?.name || item?.evidenceId || "근거 없음";
}

const SOURCE_LABELS = {
  "core-on-demand-prediction": "실시간 대여 예측",
  "kakao-local": "카카오맵 장소 정보",
  "core-route-provider": "이동 경로 정보",
  "kakao-bicycle": "카카오맵 자전거 경로",
  "kma-short-forecast": "기상청 단기예보",
  "air-korea": "에어코리아 대기질",
};

const AVAILABILITY_LABELS = { HIGH: "여유", MEDIUM: "보통", LOW: "부족" };
const TRAVEL_MODE_LABELS = { WALK: "도보", BICYCLE: "자전거" };
const AIR_GRADE_LABELS = { GOOD: "좋음", MODERATE: "보통", UNHEALTHY: "나쁨", VERY_UNHEALTHY: "매우 나쁨" };

function sourceLabel(source) {
  return SOURCE_LABELS[source] || source;
}

function isTruthyText(value) {
  return value === true || value === "true";
}

function evidenceSummary(item) {
  const text = item?.textFacts || {};
  const numeric = item?.numericFacts || {};
  const rows = (() => {
    switch (item?.kind) {
      case "rentalCandidates":
        return [
          hasValue(numeric.rentalProbability) ? ["대여 가능성", formatProbability(numeric.rentalProbability)] : null,
          hasValue(numeric.availableBikeCount) ? ["보유 대수", `${numeric.availableBikeCount}대`] : null,
          text.availabilityLevel ? ["재고 상태", AVAILABILITY_LABELS[text.availabilityLevel] || text.availabilityLevel] : null,
        ];
      case "pois":
        return [
          text.address ? ["주소", text.address] : null,
          text.category ? ["카테고리", text.category] : null,
        ];
      case "routes":
        return [
          text.travelMode ? ["이동 방식", TRAVEL_MODE_LABELS[text.travelMode] || text.travelMode] : null,
          hasValue(numeric.distanceMeters) ? ["거리", formatDistance(numeric.distanceMeters)] : null,
          hasValue(numeric.durationSeconds) ? ["시간", formatDuration(numeric.durationSeconds)] : null,
        ];
      case "weather":
        return [
          hasValue(numeric.temperatureCelsius) ? ["기온", `${numeric.temperatureCelsius}°C`] : null,
          hasValue(numeric.precipitationProbabilityPercent) ? ["강수 확률", `${numeric.precipitationProbabilityPercent}%`] : null,
          hasValue(text.isRainy) ? ["비 소식", isTruthyText(text.isRainy) ? "있음" : "없음"] : null,
        ];
      case "airQuality":
        return [
          text.khaiGrade ? ["통합대기지수", AIR_GRADE_LABELS[text.khaiGrade] || text.khaiGrade] : null,
          text.measurementStation ? ["측정소", text.measurementStation] : null,
        ];
      default:
        return [];
    }
  })();
  return rows.filter(Boolean);
}

function segmentTitle(segment, evidence, intent) {
  if (segment.type === "RENT" && segment.rentalFacts?.stationName) return segment.rentalFacts.stationName;
  const from = evidenceById(evidence, segment.fromEvidenceId);
  const to = evidenceById(evidence, segment.toEvidenceId);
  if (segment.type === "ACCESS") return `${intent?.origin?.displayName || evidenceTitle(from)} → ${segment.rentalFacts?.stationName || evidenceTitle(to)}`;
  if (segment.type === "RIDE") return `${evidenceTitle(from)} → ${evidenceTitle(to)}`;
  return evidenceTitle(to || from);
}

function Summary({ plan }) {
  const segments = plan.segments || [];
  const durations = segments.map((segment) => segment.durationSeconds).filter(hasValue);
  const distances = segments.map((segment) => segment.distanceMeters).filter(hasValue);
  const rent = segments.find((segment) => segment.type === "RENT")?.rentalFacts;
  const totalDuration = durations.length ? durations.reduce((sum, value) => sum + Number(value), 0) : null;
  const totalDistance = distances.length ? distances.reduce((sum, value) => sum + Number(value), 0) : null;
  return <section className="cr22-journey__result-summary" aria-labelledby="result-summary-title">
    <h2 id="result-summary-title"><ConsumerIcon name="check" size={19} /> 전체 일정 요약</h2>
    <div>
      <p><span>구간 시간 합계</span><strong>{formatDuration(totalDuration)}</strong></p>
      <p><span>구간 거리 합계</span><strong>{formatDistance(totalDistance)}</strong></p>
      <p><span>선택 대여소</span><strong>{present(rent?.stationName)}</strong></p>
      <p><span>대여 가능성</span><strong>{formatProbability(rent?.rentalProbability)}</strong></p>
    </div>
  </section>;
}

function Timeline({ intent, plan }) {
  return <section className="cr22-journey__timeline" aria-labelledby="timeline-title">
    <h2 id="timeline-title">시간순 일정</h2>
    {plan.segments?.length ? <ol>{plan.segments.map((segment) => {
      const [label, code, icon] = SEGMENT_COPY[segment.type] || [segment.type, segment.type, "info"];
      // RENT is an instant, stationary action (no travel), so duration/distance never apply — its
      // own rental-facts row already carries the meaningful data. VISIT has a real dwell time but
      // never a distance. Only ACCESS/RIDE are actual movement, where both apply.
      const showTime = segment.type !== "RENT";
      const showDistance = segment.type === "ACCESS" || segment.type === "RIDE";
      return <li className={`is-${String(segment.type).toLowerCase()}`} key={segment.segmentId}>
        <div className="cr22-journey__time"><strong>{formatTime(segment.startAt)}</strong>{segment.endAt ? <span>– {formatTime(segment.endAt)}</span> : null}<StatusBadge tone={segment.type === "VISIT" ? "premium" : segment.type === "ACCESS" ? "info" : "positive"}>{code}</StatusBadge></div>
        <article><span className="cr22-journey__segment-icon" aria-hidden="true"><ConsumerIcon name={icon} /></span><div><h3>{segmentTitle(segment, plan.evidence, intent)}</h3><p>{label}</p>{showTime || showDistance ? <div className="cr22-journey__facts">{showTime ? <span>시간 {formatDuration(segment.durationSeconds ?? (hasValue(segment.stayMinutes) ? Number(segment.stayMinutes) * 60 : null))}</span> : null}{showDistance ? <span>거리 {formatDistance(segment.distanceMeters)}</span> : null}{segment.travelMode ? <span>이동 {segment.travelMode}</span> : null}</div> : null}{segment.type === "RENT" ? <div className="cr22-journey__rental-facts"><span>필요 {present(segment.rentalFacts?.requiredBikeCount, (value) => `${value}대`)}</span><span>현재 {present(segment.rentalFacts?.availableBikeCount, (value) => `${value}대`)}</span><span>가능성 {formatProbability(segment.rentalFacts?.rentalProbability)}</span></div> : null}</div></article>
      </li>;
    })}</ol> : <AsyncState state="empty" title="표시할 일정 구간이 없습니다" description="백엔드가 제공한 구간이 없어 시간이나 경로를 만들지 않았습니다." />}
  </section>;
}

function Rationale({ plan, serverBuilt }) {
  const items = evidenceEntries(plan.evidence);
  const rationale = plan.rationale === "STRUCTURED_SERVER_SELECTION"
    ? "확인된 대여소·장소·경로 근거만으로 서버가 구성한 일정입니다."
    : plan.rationale;
  return <div className="cr22-journey__evidence-body">
    <p className="cr22-journey__rationale">{rationale || "추천 이유가 제공되지 않았습니다."}</p>
    <div className="cr22-journey__evidence-list" aria-label="추천 근거 상세">{items.length ? items.map((item) => { const summary = evidenceSummary(item); return <p key={`${item.kind}-${item.evidenceId}`}><span><strong>{sourceLabel(item.source)}</strong>{summary.length ? <small className="cr22-journey__evidence-facts">{summary.map(([label, value]) => `${label} ${value}`).join(" · ")}</small> : null}</span><StatusBadge tone={item.status === "NORMAL" ? "positive" : item.status === "UNAVAILABLE" || item.status === "MISSING" ? "danger" : "caution"}>{item.status}</StatusBadge></p>; }) : <p><span><strong>근거 목록 없음</strong><small>UNAVAILABLE</small></span><StatusBadge tone="danger">UNAVAILABLE</StatusBadge></p>}</div>
    <p className="cr22-journey__muted">PARTIAL·MISSING·UNAVAILABLE은 정상 근거와 구분해 그대로 표시합니다.</p>
  </div>;
}

// The evidence list is 27 cards on a busy plan. It held a whole column of a
// 695px screen for something the reader opens once, so it moved behind a button.
function EvidenceDialog({ aiFailed, onClose, open, plan, serverBuilt }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => closeRef.current?.focus());
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previous; document.removeEventListener("keydown", onKey); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="cr22-journey__evidence-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section aria-labelledby="journey-evidence-title" aria-modal="true" className="cr22-journey__evidence-dialog" role="dialog">
        <header>
          <h2 id="journey-evidence-title">{aiFailed ? "확인된 실제 근거" : serverBuilt ? "추천 이유" : "AI 추천 이유"}</h2>
          <button aria-label="추천 이유 닫기" className="cr22-journey__evidence-close" onClick={onClose} ref={closeRef} type="button">×</button>
        </header>
        <Rationale plan={plan} serverBuilt={serverBuilt} />
      </section>
    </div>
  );
}

function ResultContent({ adapter, decision, mapRenderer, now, onNavigate, onSaved, onUpdated, recheckAdapter }) {
  const plan = decision.unifiedPlan;
  const intent = decision.normalizedIntent || {};
  const constraints = intent.constraints || {};
  const [editor, setEditor] = useState({
    availableMinutes: String(constraints.availableMinutes ?? intent.maxJourneyMinutes ?? intent.totalMinutes ?? 60),
    themes: constraints.themes || [],
    stopCount: String(constraints.stopCount ?? 1),
    routeMode: constraints.routeMode || "BIKE_ONLY",
  });
  const [action, setAction] = useState("");
  const [notice, setNotice] = useState("");
  const [savedJourneyId, setSavedJourneyId] = useState(null);
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  async function replan() {
    const availableMinutes = Number(editor.availableMinutes);
    const stopCount = Number(editor.stopCount);
    if (!Number.isInteger(availableMinutes) || availableMinutes < 1 || availableMinutes > 480 || !Number.isInteger(stopCount) || stopCount < 1 || stopCount > 3) {
      setNotice("이용 시간은 1~480분, 방문 장소는 1~3곳으로 선택해 주세요.");
      return;
    }
    setAction("replan"); setNotice("");
    try { const next = await adapter.replan(decision, { constraints: { availableMinutes, themes: editor.themes, stopCount, routeMode: editor.routeMode } }); onUpdated(next); }
    catch (error) { setNotice(ERROR_COPY[error.code] || "현재 조건으로 다시 계획하지 못했습니다."); }
    finally { setAction(""); }
  }
  async function save() {
    setAction("save"); setNotice("");
    try {
      const saved = await adapter.saveCurrentConditions(decision);
      setSavedJourneyId(saved.savedJourneyId);
      onSaved?.(saved);
      setNotice("현재 계획의 재실행 입력을 저장했습니다. 다시 열 때는 최신 근거로 새 계획을 만듭니다.");
    }
    catch (error) { setNotice(ERROR_COPY[error.code] || "현재 조건을 저장하지 못했습니다."); }
    finally { setAction(""); }
  }
  async function createRecheck(departureAt) {
    if (!savedJourneyId) return;
    setAction("recheck"); setNotice("");
    try {
      await recheckAdapter.createPlanRecheck(savedJourneyId, departureAt);
      setRecheckOpen(false);
      setNotice("선택한 출발 시각의 15분 전 재확인 알림을 신청했습니다.");
    } catch (error) { setNotice(ERROR_COPY[error.code] || "재확인 알림을 신청하지 못했습니다. 다시 시도해 주세요."); }
    finally { setAction(""); }
  }
  const origin = intent.origin?.displayName;
  const destination = intent.destination?.displayName;
  const title = [origin, destination].filter(Boolean).join(" → ") || "AI 라이딩 계획";
  const serverBuiltSchedule = (decision.warnings || []).includes("AI_SCHEDULE_FALLBACK");
  // The banner row cost a full width of the screen to say what a badge beside
  // the title says. The wording moves to the subtitle so the state is never
  // dropped, only made smaller (reviewer, 2026-09-05).
  const planStatus = [plan?.status, decision.status].includes("UNAVAILABLE") ? "UNAVAILABLE"
    : [plan?.status, decision.status].includes("PARTIAL") ? "PARTIAL" : null;
  const aiScheduleFailed = planStatus === "UNAVAILABLE"
    && (decision.warnings || []).some((warning) => warning.startsWith("AI_"));
  const statusCopy = aiScheduleFailed ? "AI가 검증 가능한 일정을 만들지 못했습니다. 아래에는 확인된 실제 근거만 표시합니다."
    : planStatus === "UNAVAILABLE" ? "전체 일정은 만들지 못했습니다. 아래는 확인된 사실 구간과 근거입니다."
    : planStatus === "PARTIAL" ? "일부 근거만 확인되었습니다. 확인되지 않은 값은 따로 표시합니다."
    : "실제 대여·장소·경로 근거로 구성된 현재 계획입니다.";
  if (!plan) return <AsyncState state="partial" title="통합 일정을 표시할 수 없습니다" description="백엔드가 통합 일정이나 근거를 제공하지 않았습니다." onAction={() => onNavigate?.("planner")} actionLabel="조건 다시 입력" />;
  return <>
    <div className="cr22-journey__result-title"><div><p className="cr22-journey__breadcrumb"><ConsumerIcon name="home" size={15} /> <span aria-hidden="true">›</span> AI 플래너 <span aria-hidden="true">›</span> 결과</p><h1>{title} <StatusBadge tone="premium">PREMIUM</StatusBadge></h1><p className="cr22-journey__result-status" role="status">{serverBuiltSchedule ? <StatusBadge tone="caution">AI 미적용</StatusBadge> : null}{aiScheduleFailed ? <StatusBadge tone="danger">AI 일정 실패</StatusBadge> : null}{planStatus ? <StatusBadge tone={planStatus === "UNAVAILABLE" ? "danger" : "caution"}>{planStatus}</StatusBadge> : null}<span>{statusCopy}</span></p></div><div><ConsumerButton variant="secondary" icon={<ConsumerIcon name="info" />} onClick={() => setEvidenceOpen(true)}>{aiScheduleFailed ? "확인된 근거" : serverBuiltSchedule ? "추천 이유" : "AI 추천 이유"}</ConsumerButton><ConsumerButton variant="secondary" icon={<ConsumerIcon name="retry" />} onClick={() => { const panel = document.getElementById("structured-replan"); if (!panel) return; panel.open = true; panel.scrollIntoView({ block: "nearest" }); }}>조건 변경 후 재추천</ConsumerButton><ConsumerButton icon={<ConsumerIcon name="plan" />} disabled={Boolean(action)} loading={action === "save"} loadingLabel="저장 중…" onClick={save}>이 계획 저장</ConsumerButton></div></div>
    {savedJourneyId ? <ConsumerButton variant="secondary" disabled={Boolean(action)} onClick={() => setRecheckOpen(true)}>알림 신청</ConsumerButton> : null}
    <Summary plan={plan} />
    <div className="cr22-journey__result-layout"><div><Timeline intent={intent} plan={plan} /></div><ConsumerJourneyMap mapRenderer={mapRenderer} segments={plan.segments} /></div>
    <details className="cr22-card cr22-journey__replan-card" id="structured-replan">
      <summary className="cr22-card__header"><h2>구조화 조건으로 다시 계획</h2></summary>
      <div className="cr22-journey__replan">
        <label>이용 시간<input name="availableMinutes" autoComplete="off" type="number" min="1" max="480" value={editor.availableMinutes} onChange={(event) => setEditor((current) => ({ ...current, availableMinutes: event.target.value }))} /></label>
        <fieldset className="cr22-journey__theme-field"><legend>테마</legend><div>{THEME_OPTIONS.map(([value, label]) => <label key={value}><input name="themes" type="checkbox" value={value} checked={editor.themes.includes(value)} onChange={(event) => setEditor((current) => ({ ...current, themes: event.target.checked ? [...current.themes, value] : current.themes.filter((item) => item !== value) }))} />{label}</label>)}</div></fieldset>
        <label>방문 장소 수<select name="stopCount" autoComplete="off" value={editor.stopCount} onChange={(event) => setEditor((current) => ({ ...current, stopCount: event.target.value }))}>{[1, 2, 3].map((value) => <option key={value} value={value}>{value}곳</option>)}</select></label>
        <label>경로 방식<select name="routeMode" autoComplete="off" value={editor.routeMode} onChange={(event) => setEditor((current) => ({ ...current, routeMode: event.target.value }))}>{ROUTE_MODE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <ConsumerButton disabled={Boolean(action)} loading={action === "replan"} loadingLabel="현재 근거 확인 중…" onClick={replan}>현재 근거로 다시 계획</ConsumerButton>
      </div>
      <p className="cr22-journey__muted">재계획은 자연어를 다시 보내지 않고 이용 시간·테마·방문 장소 수·경로 방식만 구조화해서 전송합니다.</p>{notice ? <p className="cr22-journey__notice" role="status">{notice}</p> : null}
    </details>
    <EvidenceDialog aiFailed={aiScheduleFailed} onClose={() => setEvidenceOpen(false)} open={evidenceOpen} plan={plan} serverBuilt={serverBuiltSchedule} />
    <RecheckOptInDialog busy={action === "recheck"} kind="PLAN_RECHECK" now={now} onClose={() => setRecheckOpen(false)} onConfirm={createRecheck} open={recheckOpen} />
  </>;
}

export default function ConsumerJourneyPlanResultPage({ adapter = consumerJourneyAdapter, authState = "authenticated", decisionId, initialDecision, mapRenderer, now, onLogin, onNavigate, onResult, onSaved, recheckAdapter = consumerSupportAdapter, user }) {
  const [state, setState] = useState(initialDecision ? { type: "ready", decision: initialDecision } : { type: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const readyDecision = state.type === "ready" && (!decisionId || state.decision.decisionId === decisionId) ? state.decision : null;
  useEffect(() => {
    if (!reloadKey && initialDecision && (!decisionId || initialDecision.decisionId === decisionId)) {
      setState({ type: "ready", decision: initialDecision });
      return undefined;
    }
    let active = true;
    setState({ type: "loading" });
    adapter.loadDecision(decisionId).then((decision) => {
      if (active) setState({ type: "ready", decision });
    }).catch((error) => { if (active) setState({ type: "error", error }); });
    return () => { active = false; };
  }, [adapter, decisionId, initialDecision, reloadKey]);
  useEffect(() => {
    if (readyDecision) onResult?.(readyDecision);
  }, [onResult, readyDecision]);
  return <ConsumerR2Theme className="cr22-journey">
    <ConsumerAppHeader activeItem="planner" authState={authState} onLogin={onLogin} onNavigate={onNavigate} userName={user?.name || user?.displayName} userTier={user?.tier} />
    <ConsumerContainer as="main" id="main-content" className="cr22-journey__content">
      {state.type === "loading" ? <AsyncState state="loading" title="AI 계획을 불러오는 중입니다" description="저장된 decision과 현재 근거 상태를 확인하고 있습니다." /> : null}
      {state.type === "error" ? <AsyncState state="error" title={state.error?.status === 410 ? "AI 계획이 만료되었습니다" : state.error?.status === 404 ? "AI 계획을 찾을 수 없습니다" : "AI 계획을 불러오지 못했습니다"} description={ERROR_COPY[state.error?.code] || "조건을 다시 입력하거나 잠시 후 다시 시도해 주세요."} onAction={() => setReloadKey((current) => current + 1)} /> : null}
      {state.type === "ready" ? <ResultContent key={`${state.decision.decisionId}:${state.decision.revision}`} adapter={adapter} decision={state.decision} mapRenderer={mapRenderer} now={now} onNavigate={onNavigate} onSaved={onSaved} onUpdated={(decision) => setState({ type: "ready", decision })} recheckAdapter={recheckAdapter} /> : null}
    </ConsumerContainer>
  </ConsumerR2Theme>;
}
